import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  Clock,
  FileText,
  Search,
} from 'lucide-react';
import {
  myProceduresService,
  procedureInstanceService,
  type MyProcedureRow,
  type ProcedureInstance,
} from '../../services/auditService';
import { AuditStatusBadge } from './AuditStatusBadge';
import FormControl from '../../components/common/FormControl';
import Modal from '../../components/common/Modal';
import NoDataFound from '../../components/common/NoDataFound';
import Pagination from '../../components/common/Pagination';
import Skeleton from '../../components/common/Skeleton';
import Toast, { ToastMessage } from '../../components/common/Toast';
import {
  ProcedureResponseDispatcher,
  resolveRole,
} from '../../components/audits/procedure-responses';
import { useAuth } from '../../contexts/AuthContext';
import { usePermissions } from '../../contexts/PermissionContext';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';

/* ── urgency helpers ─────────────────────────────────────────────── */
function urgency(row: MyProcedureRow): { label: string; barColor: string; dueColor: string } {
  if (row.due_date == null)             return { label: 'No due date', barColor: '#e5e7eb', dueColor: '#9ca3af' };
  if (row.is_overdue)                   return { label: `${Math.abs(row.days_remaining ?? 0)}d overdue`, barColor: '#ef4444', dueColor: '#dc2626' };
  if (row.days_remaining === 0)         return { label: 'Due today',              barColor: '#f59e0b', dueColor: '#d97706' };
  if ((row.days_remaining ?? 99) <= 3)  return { label: `${row.days_remaining}d left`, barColor: '#fbbf24', dueColor: '#d97706' };
  if ((row.days_remaining ?? 99) <= 7)  return { label: `${row.days_remaining}d left`, barColor: '#38bdf8', dueColor: '#0369a1' };
  return                                       { label: `${row.days_remaining}d left`, barColor: '#d1d5db', dueColor: '#6b7280' };
}

function roleBadgeClass(role: string): string {
  const map: Record<string, string> = {
    performer: 'adt-badge adt-badge-role-performer',
    approver:  'adt-badge adt-badge-role-approver',
    lead:      'adt-badge adt-badge-role-lead',
  };
  return map[role] ?? 'adt-badge';
}

/* ── skeleton ────────────────────────────────────────────────────── */
const SkeletonCard: React.FC = () => (
  <div className="skel-card skel-row" style={{ marginBottom: 8 }}>
    <Skeleton.Box width={4} height={44} radius={4} style={{ flexShrink: 0 }} />
    <div className="skel-card-meta" style={{ flex: 1 }}>
      <Skeleton.Box width="55%" height={13} />
      <Skeleton.Box width="35%" height={11} />
    </div>
    <Skeleton.Pill width={72} height={22} />
    <Skeleton.Pill width={64} height={22} />
    <Skeleton.Box width={70} height={11} />
  </div>
);

/* ── procedure detail modal ──────────────────────────────────────── */
interface ProcedureModalProps {
  procedureId: number | null;
  onClose: () => void;
  onSaved: () => void;
}

const ProcedureDetailModal: React.FC<ProcedureModalProps> = ({ procedureId, onClose, onSaved }) => {
  const { state } = useAuth();
  const { check } = usePermissions();
  const currentUserId = state.user?.id ?? 0;
  // Activity-grant equivalent of the old 'GRC Admin' role check (backend
  // independently enforces via require_permission on ADT_FINDINGS).
  const isGrcAdmin = check('ADT_FINDINGS', 'view+modify').allowed;

  const [instance, setInstance] = useState<ProcedureInstance | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!procedureId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setInstance(null);
    procedureInstanceService.get(procedureId)
      .then(data => { if (!cancelled) { setInstance(data); setLoading(false); } })
      .catch(e => {
        if (cancelled) return;
        setError(e?.response?.data?.detail ?? e?.message ?? 'Failed to load procedure.');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [procedureId]);

  const procedureName = instance
    ? ((instance as any).procedure_name ?? (instance as any).label ?? 'Procedure')
    : 'Procedure';

  let body: React.ReactNode;
  if (loading) {
    body = (
      <div className="skel-page">
        <Skeleton.Box width="40%" height={13} />
        <Skeleton.Box width="60%" height={11} />
      </div>
    );
  } else if (error) {
    body = <NoDataFound title="Failed to load" message={error} />;
  } else if (instance) {
    const role = resolveRole(instance, currentUserId, isGrcAdmin, instance.workflow_state);
    const procedureType = (instance as any).procedure_type ?? 'Unknown';
    const performerName = (instance as any).responsible_user_name ?? (instance as any).performer_name ?? null;
    const approverName  = (instance as any).approver_user_name  ?? (instance as any).approver_name  ?? null;
    body = (
      <ProcedureResponseDispatcher
        procedureType={procedureType}
        instance={instance}
        role={role}
        performerName={performerName}
        approverName={approverName}
        onSaved={onSaved}
      />
    );
  }

  return (
    <Modal isOpen={procedureId !== null} onClose={onClose} size="xl" closeOnEscape>
      <Modal.Header>
        Procedure Details
      </Modal.Header>
      <Modal.Body>
        {body}
      </Modal.Body>
    </Modal>
  );
};

/* ── main component ──────────────────────────────────────────────── */
const MyProceduresContent: React.FC = () => {
  const { formatDate } = useEntitySettings();
  const [rows, setRows] = useState<MyProcedureRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const [stateFilter, setStateFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [openProcedureId, setOpenProcedureId] = useState<number | null>(null);
  const [pageSize, setPageSize] = useState(25);

  useEffect(() => { setPage(1); }, [searchTerm, stateFilter, roleFilter, pageSize]);

  const fetchData = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    myProceduresService.list()
      .then(resp => { if (!cancelled) { setRows(resp.data); setLoading(false); } })
      .catch(err => {
        if (cancelled) return;
        const msg = err?.response?.data?.detail ?? err?.message ?? 'Failed to load.';
        setToast({ type: 'error', message: typeof msg === 'string' ? msg : 'Failed to load.' });
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => { return fetchData(); }, [fetchData]);

  const filtered = rows.filter(r => {
    if (stateFilter !== 'all' && r.workflow_state !== stateFilter) return false;
    if (roleFilter  !== 'all' && r.my_role        !== roleFilter)  return false;
    if (searchTerm) {
      const t = searchTerm.toLowerCase();
      if (!(r.procedure_name ?? r.label ?? '').toLowerCase().includes(t) &&
          !(r.program_name ?? '').toLowerCase().includes(t)) return false;
    }
    return true;
  });

  const overdueCount = filtered.filter(r => r.is_overdue).length;
  const todayCount   = filtered.filter(r => r.days_remaining === 0).length;
  const doneCount    = filtered.filter(r => r.workflow_state === 'Approved').length;

  const paginatedRows = filtered.slice((page - 1) * pageSize, page * pageSize);

  const handleRowClick = (id: number) => setOpenProcedureId(id);
  const handleModalClose = () => setOpenProcedureId(null);
  const handleSaved = () => { setOpenProcedureId(null); fetchData(); };

  return (
    <div className="netbody-card flex flex-col h-full min-h-[500px]">

      {/* ── Toolbar + Stats — single row ── */}
      <div className="netbody-header">
        {/* LEFT: stats pills */}
        <div className="flex items-center gap-2">
          {([
            { icon: <FileText size={13} />,     label: 'Total',     value: filtered.length, accent: '#6b7280', bg: '#f3f4f6' },
            { icon: <AlertCircle size={13} />,  label: 'Overdue',   value: overdueCount,    accent: '#dc2626', bg: '#fef2f2' },
            { icon: <Clock size={13} />,        label: 'Due Today', value: todayCount,      accent: '#d97706', bg: '#fffbeb' },
            { icon: <CheckCircle2 size={13} />, label: 'Approved',  value: doneCount,       accent: '#16a34a', bg: '#f0fdf4' },
          ] as { icon: React.ReactNode; label: string; value: number; accent: string; bg: string }[]).map(s => (
            <div
              key={s.label}
              className="adt-stat-pill"
              style={{ '--adt-stat-accent': s.accent, '--adt-stat-bg': s.bg } as React.CSSProperties}
            >
              <span className="adt-stat-pill-icon">{s.icon}</span>
              <span className="adt-stat-pill-value">{loading ? '—' : s.value}</span>
              <span className="adt-stat-pill-label">{s.label}</span>
            </div>
          ))}
        </div>

        {/* RIGHT: filters + search */}
        <div className="flex items-center gap-2 ml-auto">
          <FormControl.SearchSelect
            data-testid="SEL_my_procedures_state_filter"
            value={stateFilter}
            onChange={v => setStateFilter(String(v ?? 'all'))}
            className="filter-status"
            noSearchIcon
            options={[
              { value: 'all', label: 'All states' },
              { value: 'Draft', label: 'Draft' },
              { value: 'Submitted', label: 'Submitted' },
              { value: 'InReview', label: 'In Review' },
              { value: 'Approved', label: 'Approved' },
              { value: 'Rejected', label: 'Rejected' },
              { value: 'Reopened', label: 'Reopened' },
            ]}
          />
          <FormControl.SearchSelect
            data-testid="SEL_my_procedures_role_filter"
            value={roleFilter}
            onChange={v => setRoleFilter(String(v ?? 'all'))}
            className="filter-status"
            noSearchIcon
            options={[
              { value: 'all', label: 'All my roles' },
              { value: 'performer', label: 'Performer' },
              { value: 'approver', label: 'Approver' },
              { value: 'lead', label: 'Lead' },
            ]}
          />
          <FormControl.Input
            data-testid="INP_my_procedures_search"
            type="text"
            placeholder="Search procedures…"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            leadingIcon={<Search size={14} />}
            className="filter-search"
          />
        </div>
      </div>

      {/* ── List ── */}
      <div className="netbody-content">
        <div className="scroll-container flex-1 overflow-hidden">

          {loading ? (
            Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)

          ) : filtered.length === 0 ? (
            <NoDataFound title="No Procedures Found" message="No procedures match your filters." />

          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {paginatedRows.map(row => {
                const u = urgency(row);
                return (
                  <div
                    key={row.id}
                    data-testid={`BTN_my_procedures_row_${row.id}_open`}
                    className="adt-item-card"
                    onClick={() => handleRowClick(row.id)}
                    style={{ '--adt-bar-color': u.barColor, '--adt-due-color': u.dueColor } as React.CSSProperties}
                  >
                    <div className="adt-item-card-bar" />

                    <div className="adt-item-card-body">
                      <div className="adt-item-card-title">
                        {row.procedure_name ?? row.label ?? `Procedure #${row.id}`}
                      </div>
                      <div className="adt-item-card-sub">
                        {row.program_name && <span>{row.program_name}</span>}
                        {row.procedure_type && (
                          <span className="adt-badge adt-badge-type">{row.procedure_type}</span>
                        )}
                        {row.sample_label && (
                          <span className="adt-badge adt-badge-sample">{row.sample_label}</span>
                        )}
                      </div>
                    </div>

                    <div className="adt-item-card-tail">
                      <span className={roleBadgeClass(row.my_role ?? '')}>
                        {row.my_role ?? '—'}
                      </span>

                      <AuditStatusBadge state={row.workflow_state} size="sm" />

                      <div className="adt-item-card-due">
                        <div className="adt-item-card-due-label">{u.label}</div>
                        <div className="adt-item-card-due-date">{row.due_date ? formatDate(row.due_date) : ''}</div>
                      </div>

                      <ArrowRight size={15} style={{ color: 'var(--brand-subtle)' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <Pagination
            page={page}
            pageSize={pageSize}
            totalRecords={filtered.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            recordLabel="procedures"
          />
        </div>
      </div>

      <ProcedureDetailModal
        procedureId={openProcedureId}
        onClose={handleModalClose}
        onSaved={handleSaved}
      />

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default MyProceduresContent;
